/**
 * Grid-Layout-Vorlagen für das Creator Studio
 * Enthält 15+ vordefinierte Raster-Layouts mit Platzhaltern
 */

import type { LayoutElement } from './elementTypes';

/**
 * Hilfsfunktion zum Erstellen von SVG-Thumbnails für Layout-Vorschau
 * Zeichnet Rechtecke entsprechend der Platzhalter-Positionen
 */
function createLayoutThumbnail(
  placeholders: LayoutElement['placeholders'],
  bgColor = '#f3f4f6',
  rectColor = '#e5e7eb',
  strokeColor = '#d1d5db'
): string {
  const rects = placeholders
    .map(
      (p) =>
        `<rect x="${p.position.x}%" y="${p.position.y}%" width="${p.size.width}%" height="${p.size.height}%" fill="${rectColor}" stroke="${strokeColor}" stroke-width="1" rx="4"/>`
    )
    .join('\n    ');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none">
  <rect width="100" height="100" fill="${bgColor}"/>
  ${rects}
</svg>`;
}

/**
 * Zwei-Spalten-Layouts (verschiedene Verhältnisse)
 */
const twoColumnLayouts: LayoutElement[] = [
  {
    id: 'layout-2col-50-50',
    name: '2 Spalten (50/50)',
    type: 'layout',
    tags: ['2-spalten', 'gleichmäßig', 'symmetrisch', 'split'],
    category: '2-Spalten',
    gridType: 'columns',
    thumbnail: '',
    placeholders: [
      { id: 'left', type: 'image', position: { x: 2, y: 2 }, size: { width: 47, height: 96 }, label: 'Links' },
      { id: 'right', type: 'image', position: { x: 51, y: 2 }, size: { width: 47, height: 96 }, label: 'Rechts' },
    ],
  },
  {
    id: 'layout-2col-60-40',
    name: '2 Spalten (60/40)',
    type: 'layout',
    tags: ['2-spalten', 'asymmetrisch', 'hauptinhalt'],
    category: '2-Spalten',
    gridType: 'columns',
    thumbnail: '',
    placeholders: [
      { id: 'main', type: 'image', position: { x: 2, y: 2 }, size: { width: 56, height: 96 }, label: 'Hauptbereich' },
      { id: 'side', type: 'image', position: { x: 60, y: 2 }, size: { width: 38, height: 96 }, label: 'Nebenbereich' },
    ],
  },
  {
    id: 'layout-2col-70-30',
    name: '2 Spalten (70/30)',
    type: 'layout',
    tags: ['2-spalten', 'sidebar', 'hauptinhalt'],
    category: '2-Spalten',
    gridType: 'columns',
    thumbnail: '',
    placeholders: [
      { id: 'main', type: 'image', position: { x: 2, y: 2 }, size: { width: 66, height: 96 }, label: 'Hauptbereich' },
      { id: 'sidebar', type: 'image', position: { x: 70, y: 2 }, size: { width: 28, height: 96 }, label: 'Sidebar' },
    ],
  },
  {
    id: 'layout-2col-30-70',
    name: '2 Spalten (30/70)',
    type: 'layout',
    tags: ['2-spalten', 'sidebar-links', 'navigation'],
    category: '2-Spalten',
    gridType: 'columns',
    thumbnail: '',
    placeholders: [
      { id: 'sidebar', type: 'image', position: { x: 2, y: 2 }, size: { width: 28, height: 96 }, label: 'Sidebar' },
      { id: 'main', type: 'image', position: { x: 32, y: 2 }, size: { width: 66, height: 96 }, label: 'Hauptbereich' },
    ],
  },
];

/**
 * Drei-Spalten-Layouts
 */
const threeColumnLayouts: LayoutElement[] = [
  {
    id: 'layout-3col-equal',
    name: '3 Spalten (gleich)',
    type: 'layout',
    tags: ['3-spalten', 'gleichmäßig', 'dreiteiler'],
    category: '3-Spalten',
    gridType: 'columns',
    thumbnail: '',
    placeholders: [
      { id: 'col1', type: 'image', position: { x: 2, y: 2 }, size: { width: 30, height: 96 }, label: 'Spalte 1' },
      { id: 'col2', type: 'image', position: { x: 35, y: 2 }, size: { width: 30, height: 96 }, label: 'Spalte 2' },
      { id: 'col3', type: 'image', position: { x: 68, y: 2 }, size: { width: 30, height: 96 }, label: 'Spalte 3' },
    ],
  },
  {
    id: 'layout-3col-1-2-1',
    name: '3 Spalten (1-2-1)',
    type: 'layout',
    tags: ['3-spalten', 'fokus', 'mitte-betont'],
    category: '3-Spalten',
    gridType: 'columns',
    thumbnail: '',
    placeholders: [
      { id: 'left', type: 'image', position: { x: 2, y: 2 }, size: { width: 22, height: 96 }, label: 'Links' },
      { id: 'center', type: 'image', position: { x: 26, y: 2 }, size: { width: 48, height: 96 }, label: 'Mitte' },
      { id: 'right', type: 'image', position: { x: 76, y: 2 }, size: { width: 22, height: 96 }, label: 'Rechts' },
    ],
  },
];

/**
 * Hero-Layouts mit großem Bild + kleineren Elementen
 */
const heroLayouts: LayoutElement[] = [
  {
    id: 'layout-hero-2cards',
    name: 'Hero + 2 Karten',
    type: 'layout',
    tags: ['hero', 'feature', 'karten', 'showcase'],
    category: 'Hero',
    gridType: 'mixed',
    thumbnail: '',
    placeholders: [
      { id: 'hero', type: 'image', position: { x: 2, y: 2 }, size: { width: 96, height: 58 }, label: 'Hero-Bild' },
      { id: 'card1', type: 'image', position: { x: 2, y: 62 }, size: { width: 47, height: 36 }, label: 'Karte 1' },
      { id: 'card2', type: 'image', position: { x: 51, y: 62 }, size: { width: 47, height: 36 }, label: 'Karte 2' },
    ],
  },
  {
    id: 'layout-hero-3cards',
    name: 'Hero + 3 Karten',
    type: 'layout',
    tags: ['hero', 'feature', 'karten', 'galerie'],
    category: 'Hero',
    gridType: 'mixed',
    thumbnail: '',
    placeholders: [
      { id: 'hero', type: 'image', position: { x: 2, y: 2 }, size: { width: 96, height: 54 }, label: 'Hero-Bild' },
      { id: 'card1', type: 'image', position: { x: 2, y: 58 }, size: { width: 30, height: 40 }, label: 'Karte 1' },
      { id: 'card2', type: 'image', position: { x: 35, y: 58 }, size: { width: 30, height: 40 }, label: 'Karte 2' },
      { id: 'card3', type: 'image', position: { x: 68, y: 58 }, size: { width: 30, height: 40 }, label: 'Karte 3' },
    ],
  },
  {
    id: 'layout-hero-side',
    name: 'Hero + Seitenpanel',
    type: 'layout',
    tags: ['hero', 'sidebar', 'text-bild'],
    category: 'Hero',
    gridType: 'mixed',
    thumbnail: '',
    placeholders: [
      { id: 'hero', type: 'image', position: { x: 2, y: 2 }, size: { width: 64, height: 96 }, label: 'Hero-Bild' },
      { id: 'text1', type: 'text', position: { x: 68, y: 2 }, size: { width: 30, height: 30 }, label: 'Text oben' },
      { id: 'image1', type: 'image', position: { x: 68, y: 34 }, size: { width: 30, height: 30 }, label: 'Bild' },
      { id: 'text2', type: 'text', position: { x: 68, y: 66 }, size: { width: 30, height: 32 }, label: 'Text unten' },
    ],
  },
];

/**
 * Masonry/Pinterest-Style Layouts
 */
const masonryLayouts: LayoutElement[] = [
  {
    id: 'layout-masonry-4',
    name: 'Masonry (4 Bilder)',
    type: 'layout',
    tags: ['masonry', 'pinterest', 'galerie', 'gestapelt'],
    category: 'Masonry',
    gridType: 'masonry',
    thumbnail: '',
    placeholders: [
      { id: 'tall1', type: 'image', position: { x: 2, y: 2 }, size: { width: 47, height: 62 }, label: 'Groß 1' },
      { id: 'small1', type: 'image', position: { x: 2, y: 66 }, size: { width: 47, height: 32 }, label: 'Klein 1' },
      { id: 'small2', type: 'image', position: { x: 51, y: 2 }, size: { width: 47, height: 32 }, label: 'Klein 2' },
      { id: 'tall2', type: 'image', position: { x: 51, y: 36 }, size: { width: 47, height: 62 }, label: 'Groß 2' },
    ],
  },
  {
    id: 'layout-masonry-5',
    name: 'Masonry (5 Bilder)',
    type: 'layout',
    tags: ['masonry', 'pinterest', 'galerie', 'kollage'],
    category: 'Masonry',
    gridType: 'masonry',
    thumbnail: '',
    placeholders: [
      { id: 'big', type: 'image', position: { x: 2, y: 2 }, size: { width: 47, height: 62 }, label: 'Groß' },
      { id: 'top1', type: 'image', position: { x: 51, y: 2 }, size: { width: 47, height: 30 }, label: 'Oben Rechts' },
      { id: 'mid1', type: 'image', position: { x: 51, y: 34 }, size: { width: 23, height: 30 }, label: 'Mitte Links' },
      { id: 'mid2', type: 'image', position: { x: 75, y: 34 }, size: { width: 23, height: 30 }, label: 'Mitte Rechts' },
      { id: 'bottom', type: 'image', position: { x: 2, y: 66 }, size: { width: 96, height: 32 }, label: 'Unten' },
    ],
  },
  {
    id: 'layout-masonry-6',
    name: 'Masonry (6 Bilder)',
    type: 'layout',
    tags: ['masonry', 'pinterest', 'galerie', 'grid'],
    category: 'Masonry',
    gridType: 'masonry',
    thumbnail: '',
    placeholders: [
      { id: 'a1', type: 'image', position: { x: 2, y: 2 }, size: { width: 30, height: 46 }, label: 'A1' },
      { id: 'a2', type: 'image', position: { x: 2, y: 50 }, size: { width: 30, height: 48 }, label: 'A2' },
      { id: 'b1', type: 'image', position: { x: 35, y: 2 }, size: { width: 30, height: 62 }, label: 'B1' },
      { id: 'b2', type: 'image', position: { x: 35, y: 66 }, size: { width: 30, height: 32 }, label: 'B2' },
      { id: 'c1', type: 'image', position: { x: 68, y: 2 }, size: { width: 30, height: 32 }, label: 'C1' },
      { id: 'c2', type: 'image', position: { x: 68, y: 36 }, size: { width: 30, height: 62 }, label: 'C2' },
    ],
  },
];

/**
 * Sidebar-Layouts (Hauptinhalt + Nebenbereich)
 */
const sidebarLayouts: LayoutElement[] = [
  {
    id: 'layout-sidebar-right',
    name: 'Sidebar Rechts',
    type: 'layout',
    tags: ['sidebar', 'navigation', 'dashboard'],
    category: 'Sidebar',
    gridType: 'columns',
    thumbnail: '',
    placeholders: [
      { id: 'header', type: 'text', position: { x: 2, y: 2 }, size: { width: 66, height: 14 }, label: 'Header' },
      { id: 'main', type: 'image', position: { x: 2, y: 18 }, size: { width: 66, height: 80 }, label: 'Hauptinhalt' },
      { id: 'sidebar-top', type: 'text', position: { x: 70, y: 2 }, size: { width: 28, height: 30 }, label: 'Sidebar Oben' },
      { id: 'sidebar-mid', type: 'image', position: { x: 70, y: 34 }, size: { width: 28, height: 30 }, label: 'Sidebar Mitte' },
      { id: 'sidebar-bottom', type: 'text', position: { x: 70, y: 66 }, size: { width: 28, height: 32 }, label: 'Sidebar Unten' },
    ],
  },
  {
    id: 'layout-sidebar-left',
    name: 'Sidebar Links',
    type: 'layout',
    tags: ['sidebar', 'navigation', 'menü'],
    category: 'Sidebar',
    gridType: 'columns',
    thumbnail: '',
    placeholders: [
      { id: 'nav1', type: 'text', position: { x: 2, y: 2 }, size: { width: 22, height: 22 }, label: 'Nav 1' },
      { id: 'nav2', type: 'text', position: { x: 2, y: 26 }, size: { width: 22, height: 22 }, label: 'Nav 2' },
      { id: 'nav3', type: 'text', position: { x: 2, y: 50 }, size: { width: 22, height: 22 }, label: 'Nav 3' },
      { id: 'nav4', type: 'text', position: { x: 2, y: 74 }, size: { width: 22, height: 24 }, label: 'Nav 4' },
      { id: 'content', type: 'image', position: { x: 26, y: 2 }, size: { width: 72, height: 96 }, label: 'Hauptinhalt' },
    ],
  },
];

/**
 * Full-Width Sektionen Layouts
 */
const fullWidthLayouts: LayoutElement[] = [
  {
    id: 'layout-fullwidth-3sections',
    name: 'Vollbreite (3 Sektionen)',
    type: 'layout',
    tags: ['vollbreite', 'sektionen', 'landing', 'scroll'],
    category: 'Vollbreite',
    gridType: 'rows',
    thumbnail: '',
    placeholders: [
      { id: 'section1', type: 'image', position: { x: 2, y: 2 }, size: { width: 96, height: 30 }, label: 'Sektion 1' },
      { id: 'section2', type: 'image', position: { x: 2, y: 34 }, size: { width: 96, height: 30 }, label: 'Sektion 2' },
      { id: 'section3', type: 'image', position: { x: 2, y: 66 }, size: { width: 96, height: 32 }, label: 'Sektion 3' },
    ],
  },
  {
    id: 'layout-fullwidth-hero-content',
    name: 'Hero + Content Blöcke',
    type: 'layout',
    tags: ['vollbreite', 'hero', 'landing', 'feature'],
    category: 'Vollbreite',
    gridType: 'rows',
    thumbnail: '',
    placeholders: [
      { id: 'hero', type: 'image', position: { x: 2, y: 2 }, size: { width: 96, height: 40 }, label: 'Hero' },
      { id: 'block1', type: 'text', position: { x: 2, y: 44 }, size: { width: 30, height: 26 }, label: 'Block 1' },
      { id: 'block2', type: 'text', position: { x: 35, y: 44 }, size: { width: 30, height: 26 }, label: 'Block 2' },
      { id: 'block3', type: 'text', position: { x: 68, y: 44 }, size: { width: 30, height: 26 }, label: 'Block 3' },
      { id: 'footer', type: 'image', position: { x: 2, y: 72 }, size: { width: 96, height: 26 }, label: 'Footer' },
    ],
  },
  {
    id: 'layout-fullwidth-alternating',
    name: 'Alternierend (Zickzack)',
    type: 'layout',
    tags: ['vollbreite', 'alternierend', 'zickzack', 'about'],
    category: 'Vollbreite',
    gridType: 'mixed',
    thumbnail: '',
    placeholders: [
      { id: 'img1', type: 'image', position: { x: 2, y: 2 }, size: { width: 47, height: 30 }, label: 'Bild 1' },
      { id: 'text1', type: 'text', position: { x: 51, y: 2 }, size: { width: 47, height: 30 }, label: 'Text 1' },
      { id: 'text2', type: 'text', position: { x: 2, y: 34 }, size: { width: 47, height: 30 }, label: 'Text 2' },
      { id: 'img2', type: 'image', position: { x: 51, y: 34 }, size: { width: 47, height: 30 }, label: 'Bild 2' },
      { id: 'img3', type: 'image', position: { x: 2, y: 66 }, size: { width: 47, height: 32 }, label: 'Bild 3' },
      { id: 'text3', type: 'text', position: { x: 51, y: 66 }, size: { width: 47, height: 32 }, label: 'Text 3' },
    ],
  },
];

/**
 * Spezielle Grid-Layouts
 */
const specialLayouts: LayoutElement[] = [
  {
    id: 'layout-grid-4x4',
    name: 'Raster (2x2)',
    type: 'layout',
    tags: ['grid', 'raster', 'gleichmäßig', 'quadrat'],
    category: 'Grid',
    gridType: 'mixed',
    thumbnail: '',
    placeholders: [
      { id: 'tl', type: 'image', position: { x: 2, y: 2 }, size: { width: 47, height: 47 }, label: 'Oben Links' },
      { id: 'tr', type: 'image', position: { x: 51, y: 2 }, size: { width: 47, height: 47 }, label: 'Oben Rechts' },
      { id: 'bl', type: 'image', position: { x: 2, y: 51 }, size: { width: 47, height: 47 }, label: 'Unten Links' },
      { id: 'br', type: 'image', position: { x: 51, y: 51 }, size: { width: 47, height: 47 }, label: 'Unten Rechts' },
    ],
  },
  {
    id: 'layout-featured-grid',
    name: 'Featured + Grid',
    type: 'layout',
    tags: ['featured', 'grid', 'highlight', 'produkt'],
    category: 'Grid',
    gridType: 'mixed',
    thumbnail: '',
    placeholders: [
      { id: 'featured', type: 'image', position: { x: 2, y: 2 }, size: { width: 64, height: 64 }, label: 'Featured' },
      { id: 'thumb1', type: 'image', position: { x: 68, y: 2 }, size: { width: 30, height: 30 }, label: 'Thumb 1' },
      { id: 'thumb2', type: 'image', position: { x: 68, y: 34 }, size: { width: 30, height: 30 }, label: 'Thumb 2' },
      { id: 'thumb3', type: 'image', position: { x: 2, y: 68 }, size: { width: 30, height: 30 }, label: 'Thumb 3' },
      { id: 'thumb4', type: 'image', position: { x: 35, y: 68 }, size: { width: 30, height: 30 }, label: 'Thumb 4' },
      { id: 'thumb5', type: 'image', position: { x: 68, y: 68 }, size: { width: 30, height: 30 }, label: 'Thumb 5' },
    ],
  },
];

/**
 * Alle Layout-Vorlagen mit generierten Thumbnails
 */
const allLayouts = [
  ...twoColumnLayouts,
  ...threeColumnLayouts,
  ...heroLayouts,
  ...masonryLayouts,
  ...sidebarLayouts,
  ...fullWidthLayouts,
  ...specialLayouts,
];

// Thumbnails für alle Layouts generieren
export const LAYOUT_LIBRARY: LayoutElement[] = allLayouts.map((layout) => ({
  ...layout,
  thumbnail: createLayoutThumbnail(layout.placeholders),
}));

/**
 * Layout nach ID abrufen
 */
export function getLayoutById(id: string): LayoutElement | undefined {
  return LAYOUT_LIBRARY.find((layout) => layout.id === id);
}

/**
 * Layouts nach Kategorie filtern
 */
export function getLayoutsByCategory(category: string): LayoutElement[] {
  return LAYOUT_LIBRARY.filter((layout) => layout.category === category);
}

/**
 * Layouts nach Tags suchen
 */
export function searchLayouts(query: string): LayoutElement[] {
  const lowerQuery = query.toLowerCase();
  return LAYOUT_LIBRARY.filter(
    (layout) =>
      layout.name.toLowerCase().includes(lowerQuery) ||
      layout.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)) ||
      (layout.category && layout.category.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Alle verfügbaren Kategorien
 */
export const LAYOUT_CATEGORIES = [
  '2-Spalten',
  '3-Spalten',
  'Hero',
  'Masonry',
  'Sidebar',
  'Vollbreite',
  'Grid',
] as const;

export type LayoutCategory = (typeof LAYOUT_CATEGORIES)[number];
